import { Trans, useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Card } from "react-bootstrap";
import { FaLock } from "react-icons/fa";
import { startAuthentication } from "@simplewebauthn/browser";
import type { AuthenticationResponseJSON } from "@simplewebauthn/types";
import apiClient from "@shared/services/apiClient";
import logger from "@shared/utils/logger";
import { useAuth } from "@shared/context/AuthContext";
import { getLockScreenBranding } from "@shared/config/lockScreenBranding";
import "@shared/styles/login-page.css";
import "@shared/styles/app-ui-auth.css";
import "./LockScreen.css";

const PASSWORD_VISIBILITY_TIMEOUT_MS = 5000;

interface LockScreenProps {
  onUnlock: () => void;
  autoLocked?: boolean;
}

const LockScreen: React.FC<LockScreenProps> = ({ onUnlock, autoLocked }) => {
  const { t } = useTranslation("features");
  const { handleLogout } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyRequired, setPasskeyRequired] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const branding = getLockScreenBranding();

  useEffect(() => {
    void fetchUnlockPolicy();
  }, []);

  const fetchUnlockPolicy = async () => {
    try {
      const response = await apiClient.get("/users/unlock-policy");
      const policy = response.data?.data || response.data;
      setPasskeyRequired(Boolean(policy?.requiresPasskeyUnlock));
      setPasskeyAvailable(Boolean(policy?.passkeyAvailable));
    } catch (err) {
      logger.warn("Failed to fetch lock screen unlock policy", err);
      setError(t("ui.copy.d6a2e9c0b781"));
    }
  };

  const handleShowPasswordToggle = () => {
    setShowPassword((previous) => !previous);
  };

  useEffect(() => {
    if (!showPassword) return;
    const timeoutId = window.setTimeout(() => {
      setShowPassword(false);
    }, PASSWORD_VISIBILITY_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showPassword]);

  const getUnlockErrorMessage = (err: unknown): string => {
    const status = (err as any)?.response?.status;
    if (status === 401 || status === 429) {
      return t("ui.copy.e7b3f0d1c892");
    }
    if (status === 403) {
      const code = (err as any)?.response?.data?.details?.code;
      if (code === "PASSKEY_REQUIRED") {
        return t("ui.copy.f8c4a1e2d903");
      }
    }
    return t("ui.copy.e7b3f0d1c892");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passkeyRequired) {
      setError(t("ui.copy.f8c4a1e2d903"));
      return;
    }

    setError("");
    setLoading(true);

    try {
      await apiClient.post("/users/verify-password", { password });
      onUnlock();
    } catch (err: any) {
      logger.warn("Lock screen unlock verification failed", err);
      setError(getUnlockErrorMessage(err));
      setPassword("");
      setShowPassword(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyUnlock = async () => {
    setError("");
    setPasskeyLoading(true);

    try {
      const optionsResponse = await apiClient.post(
        "/users/unlock/passkey/options",
      );
      const options = optionsResponse.data?.data?.options;
      const challengeId = optionsResponse.data?.data?.challengeId;
      const mode = optionsResponse.data?.data?.mode;
      if (!options) {
        throw new Error("Missing passkey options");
      }
      if (
        options.allowCredentials === undefined ||
        options.allowCredentials === null
      ) {
        delete options.allowCredentials;
      }

      let authenticationResponse: AuthenticationResponseJSON;
      try {
        authenticationResponse = await startAuthentication(options);
      } catch (webauthnError: any) {
        if (webauthnError?.name === "NotAllowedError") {
          setError(t("ui.copy.a9d5b2f3e014"));
          return;
        }
        throw webauthnError;
      }

      await apiClient.post("/users/unlock/passkey/verify", {
        response: authenticationResponse,
        challengeId,
        mode,
      });

      onUnlock();
    } catch (err) {
      logger.warn("Lock screen passkey unlock failed", err);
      setError(t("ui.copy.e7b3f0d1c892"));
    } finally {
      setPasskeyLoading(false);
    }
  };

  const passwordToggleLabel = showPassword
    ? t("ui.copy.b0e6c3a4f125")
    : t("ui.copy.c1f7d4b5a236");

  return (
    <div className="lock-screen-overlay login-container app-ui-auth-surface">
      <div
        className="lock-screen-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lock-screen-title"
        aria-describedby="lock-screen-description"
      >
        <Card className="login-card lock-screen-card">
          <Card.Body className="login-card-body lock-screen-card-body">
            <div className="lock-screen-brand">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={t("ui.copy.d2a8e5c6b347")}
                  className="lock-screen-logo"
                />
              ) : (
                <FaLock className="lock-screen-brand-icon" aria-hidden="true" />
              )}
            </div>

            <h1 id="lock-screen-title" className="lock-screen-title login-title-strong">
              <Trans ns="features" i18nKey="ui.copy.d14b04fceb10" />
            </h1>

            <p className="lock-screen-org">{branding.organizationDisplayName}</p>

            <p
              id="lock-screen-description"
              className="lock-screen-subtitle text-muted"
            >
              <Trans ns="features" i18nKey="ui.copy.fceff54dcf2e" />
            </p>

            {autoLocked && (
              <p className="lock-screen-auto-locked text-muted">
                <Trans ns="features" i18nKey="ui.copy.462f94422014" />
              </p>
            )}

            <div className="security-inline-message lock-screen-compliance" role="note">
              <i className="bi bi-shield-lock-fill" aria-hidden="true" />
              <span>
                <Trans ns="features" i18nKey="ui.copy.b8e4c1a2f903" />
              </span>
            </div>

            {passkeyRequired && (
              <Alert variant="info" className="mb-3">
                <Trans ns="features" i18nKey="ui.copy.567954ddeab3" />
              </Alert>
            )}
            {passkeyRequired && !passkeyAvailable && (
              <Alert variant="danger" className="mb-3">
                <Trans ns="features" i18nKey="ui.copy.7e19b1a06564" />
              </Alert>
            )}

            {error && (
              <Alert variant="danger" className="mb-3" role="alert">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} autoComplete="off">
              <Form.Group className="mb-3">
                <div className="password-input-wrapper">
                  <Form.Control
                    id="lock-screen-password"
                    name="session-unlock-verification"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("ui.copy.e1b7f4d5c236")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    disabled={loading || passkeyRequired}
                    autoComplete="off"
                    className="form-control-custom lock-screen-password-input"
                    aria-label={t("ui.copy.e1b7f4d5c236")}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={handleShowPasswordToggle}
                    disabled={loading || passkeyRequired}
                    aria-label={passwordToggleLabel}
                    title={passwordToggleLabel}
                  >
                    <i className={`bi bi-eye${showPassword ? "-slash" : ""}`} />
                  </button>
                </div>
              </Form.Group>

              <Button
                type="submit"
                className="btn-login w-100"
                disabled={loading || !password || passkeyRequired}
              >
                {loading ? t("ui.copy.a3d9b6f7e458") : t("ui.copy.f2c8a5e6d347")}
              </Button>
              {passkeyAvailable && (
                <Button
                  type="button"
                  variant="outline-primary"
                  className="lock-screen-passkey-btn w-100 mt-2"
                  disabled={passkeyLoading}
                  onClick={handlePasskeyUnlock}
                >
                  {passkeyLoading
                    ? t("ui.copy.c5f1d8b9a670")
                    : t("ui.copy.b4e0c7a8f569")}
                </Button>
              )}
            </Form>

            <p className="lock-screen-hint text-muted">
              <Trans
                ns="features"
                i18nKey="ui.copy.c9f5d2b3a014"
                components={{ kbd: <kbd /> }}
              />
            </p>

            <div className="lock-screen-footer">
              <button
                type="button"
                className="lock-screen-sign-out"
                onClick={() => void handleLogout()}
              >
                <Trans ns="features" i18nKey="ui.copy.d0a6e3c4b125" />
              </button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default LockScreen;
